/**
 * AmortizationTable Component
 * Specialized table for displaying the payment schedule (amortization table)
 * Shows coupon details with status badges and a summary row
 */

import type { PaymentScheduleCoupon } from '@/types/backoffice-crud.types';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/utils/formatters';
import styles from './AmortizationTable.module.css';

interface AmortizationTableProps {
  schedule: PaymentScheduleCoupon[];
}

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getCouponStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'PAID':
      return 'success';
    case 'PARTIAL':
      return 'warning';
    case 'OVERDUE':
      return 'danger';
    case 'OVERDUE_PARTIAL':
      return 'warning';
    case 'PENDING':
    default:
      return 'default';
  }
}

function getCouponStatusLabel(status: string): string {
  switch (status) {
    case 'PAID': return 'Pagado';
    case 'PARTIAL': return 'Parcial';
    case 'OVERDUE': return 'Vencido';
    case 'OVERDUE_PARTIAL': return 'Vencido Parcial';
    case 'PENDING': return 'Pendiente';
    case 'WRITTEN_OFF': return 'Castigado';
    default: return status;
  }
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function AmortizationTable({ schedule }: AmortizationTableProps) {
  if (!schedule || schedule.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Sin tabla de amortizacion disponible.
      </p>
    );
  }

  const sorted = [...schedule].sort((a, b) => a.coupon_number - b.coupon_number);

  const totalPrincipal = sorted.reduce((sum, c) => sum + c.principal_mxn, 0);
  const totalInterest = sorted.reduce((sum, c) => sum + c.interest_mxn, 0);
  const totalIva = sorted.reduce((sum, c) => sum + (c.iva_mxn ?? 0), 0);
  const totalAmount = sorted.reduce((sum, c) => sum + c.total_mxn, 0);
  const totalPaid = sorted.reduce((sum, c) => sum + c.paid_amount_mxn, 0);
  const totalReduction = sorted.reduce((sum, c) => sum + (c.anticipated_reduction_mxn ?? 0), 0);
  const hasReductions = totalReduction > 0;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">#</th>
            <th className="px-3 py-2 font-medium">Fecha Vencimiento</th>
            <th className="px-3 py-2 text-right font-medium">Principal</th>
            {hasReductions && (
              <th className="px-3 py-2 text-right font-medium">Reducción Capital</th>
            )}
            <th className="px-3 py-2 text-right font-medium">Interes</th>
            <th className="px-3 py-2 text-right font-medium">IVA</th>
            <th className="px-3 py-2 text-right font-medium">Total</th>
            <th className="px-3 py-2 text-right font-medium">Saldo Restante</th>
            <th className="px-3 py-2 font-medium">Estado</th>
            <th className="px-3 py-2 text-right font-medium">Pagado</th>
            <th className="px-3 py-2 text-right font-medium">Pendiente</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((coupon) => {
            const isOverdue = coupon.status === 'OVERDUE' || coupon.status === 'OVERDUE_PARTIAL';
            return (
              <tr
                key={coupon.coupon_number}
                className={`border-b border-border/50 ${isOverdue ? styles.overdueRow : ''}`}
              >
                <td className="px-3 py-2 tabular-nums font-medium text-foreground">
                  {coupon.coupon_number}
                </td>
                <td className="px-3 py-2 text-foreground">
                  {formatDateShort(coupon.due_date)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {formatCurrency(coupon.principal_mxn, 'MXN', 'es-MX')}
                </td>
                {hasReductions && (
                  <td className="px-3 py-2 text-right tabular-nums text-orange-600">
                    {(coupon.anticipated_reduction_mxn ?? 0) > 0
                      ? `−${formatCurrency(coupon.anticipated_reduction_mxn!, 'MXN', 'es-MX')}`
                      : '-'}
                  </td>
                )}
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {formatCurrency(coupon.interest_mxn, 'MXN', 'es-MX')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {formatCurrency(coupon.iva_mxn ?? 0, 'MXN', 'es-MX')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-foreground">
                  {formatCurrency(coupon.total_mxn, 'MXN', 'es-MX')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {formatCurrency(coupon.remaining_balance_mxn, 'MXN', 'es-MX')}
                </td>
                <td className="px-3 py-2">
                  <Badge variant={getCouponStatusVariant(coupon.status)}>
                    {getCouponStatusLabel(coupon.status)}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {formatCurrency(coupon.paid_amount_mxn, 'MXN', 'es-MX')}
                </td>
                <td className={`px-3 py-2 text-right tabular-nums ${isOverdue ? 'text-red-600 font-medium' : 'text-foreground'}`}>
                  {formatCurrency(Math.max(0, coupon.total_mxn - (coupon.paid_amount_mxn || 0)), 'MXN', 'es-MX')}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border bg-muted/30 font-medium">
            <td className="px-3 py-2 text-foreground" colSpan={2}>
              Total
            </td>
            <td className="px-3 py-2 text-right tabular-nums text-foreground">
              {formatCurrency(totalPrincipal, 'MXN', 'es-MX')}
            </td>
            {hasReductions && (
              <td className="px-3 py-2 text-right tabular-nums text-orange-600">
                −{formatCurrency(totalReduction, 'MXN', 'es-MX')}
              </td>
            )}
            <td className="px-3 py-2 text-right tabular-nums text-foreground">
              {formatCurrency(totalInterest, 'MXN', 'es-MX')}
            </td>
            <td className="px-3 py-2 text-right tabular-nums text-foreground">
              {formatCurrency(totalIva, 'MXN', 'es-MX')}
            </td>
            <td className="px-3 py-2 text-right tabular-nums text-foreground">
              {formatCurrency(totalAmount, 'MXN', 'es-MX')}
            </td>
            <td className="px-3 py-2" />
            <td className="px-3 py-2" />
            <td className="px-3 py-2 text-right tabular-nums text-foreground">
              {formatCurrency(totalPaid, 'MXN', 'es-MX')}
            </td>
            <td className="px-3 py-2 text-right tabular-nums text-foreground">
              {formatCurrency(totalAmount - totalPaid, 'MXN', 'es-MX')}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
